import { format } from 'date-fns-tz';

import { TimeoutError } from 'data/errors/TimeoutError';
import { useAuthToken } from 'hooks/useAuthToken';
import env from 'utils/env';
import { reportError } from 'utils/errorBoundary';
import { prettifyGraphQLBadRequestError } from 'utils/errors';

export class GraphqlError extends Error {}

export const useFetchData = <TData, TVariables>(
  query: string,
  options?: RequestInit['headers']
): ((variables?: TVariables) => Promise<TData>) => {
  const authToken = useAuthToken();

  return async (variables?: TVariables) => {
    const url = `${env.API_URL}/graphql`;
    let res;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
          ...options
        },
        body: JSON.stringify({
          query,
          variables
        })
      });
    } catch (e) {
      reportError('Unhandled error in backend request: ', e as Error);
      throw e;
    }

    // Check for HTTP errors before attempting to parse JSON
    if (!res.ok) {
      const statusText = res.statusText || 'Unknown Error';
      const now = new Date();
      const timestamp = `${format(now, 'yyyy/MM/dd HH:mm:ss')} ${format(now, 'zzz')}`;
      const errorMessage = `\nGraphQL request failed with status ${res.status}: ${statusText}.\nURL: ${url}.\nTimestamp: ${timestamp}`;

      // Special handling for 504 Gateway Timeout errors
      // These often mean the request succeeded but took too long to respond
      if (res.status === 504) {
        const timeoutError = new TimeoutError(errorMessage);
        reportError(errorMessage, timeoutError);
        throw timeoutError;
      }

      reportError(errorMessage, new Error(errorMessage));
      throw new GraphqlError(errorMessage);
    }

    /* eslint-disable */
    const json = await res.json();

    // TODO: error message could be WAY better
    if (json.errors) {
      const { message, extensions } = json.errors[0] || {};
      if (extensions?.code === 'BAD_USER_INPUT' && extensions.response) {
        // returned error message
        const detailedError = prettifyGraphQLBadRequestError(extensions.response.message);
        throw new GraphqlError(detailedError);
      }
      // For errors whose path contains "body" (e.g. a bad enum value deep inside a
      // ScheduledContent body), null out that node's body and return the rest of the
      // data. This is schema-agnostic and keeps the surrounding valid history entries
      // intact while causing isUsableItem to drop the affected entry.
      //
      // For all other partial errors (e.g. the top-level pageDTOByAllessehId query),
      // fall through to the throw path below so callers see a real error.
      if (json.data) {
        let handledPartialErrors = false;
        for (const err of json.errors as Array<{ path?: unknown[]; message?: string }>) {
          if (!Array.isArray(err.path)) continue;
          const bodyIdx = err.path.indexOf('body');
          if (bodyIdx < 1) continue;
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let cursor: any = json.data;
            for (let i = 0; i < bodyIdx - 1; i++) cursor = cursor?.[err.path[i] as string | number];
            const nodeKey = err.path[bodyIdx - 1] as string | number;
            if (cursor?.[nodeKey] && typeof cursor[nodeKey] === 'object') {
              cursor[nodeKey].body = null;
              cursor[nodeKey].bodyError = err.message ?? 'Unknown error';
              handledPartialErrors = true;
            }
          } catch {
            // path-walking failed — leave data as-is
          }
        }
        if (handledPartialErrors) {
          reportError('Partial GraphQL errors in response: ', json);
          return json.data;
        }
      }
      // Check if backend provided the target URL it was trying to connect to
      if (extensions?.targetUrl) {
        const now = new Date();
        const timestamp = `${format(now, 'yyyy/MM/dd HH:mm:ss')} ${format(now, 'zzz')}`;
        const statusCode = extensions.statusCode || 'unknown status';
        const errorMessage = `\nRequest to external service failed.\nTarget URL: ${
          extensions.targetUrl
        }\nStatus: ${statusCode}\nTimestamp: ${timestamp}\nDetails: ${message || 'Unknown error'}`;
        reportError(errorMessage, new Error(errorMessage));
        throw new GraphqlError(errorMessage);
      }
      reportError('Unhandled error in backend request: ', json);
      throw new Error(message || 'Error...');
    }

    return json.data;
    /* eslint-enable */
  };
};
